import { normalizeDeliveryEmail } from "./email";

export interface EmailRecipient { name: string; email: string }
export interface RecipientSet { to: EmailRecipient[]; cc: EmailRecipient[] }
export interface RecipientGroup extends RecipientSet { id: string; name: string; isDefault: boolean; version: number }
export interface LastRecipients extends RecipientSet { document: string; kind: "invoice" | "estimate"; sentAt: string }
export interface RecipientSource extends RecipientSet { key: string }
export interface RecipientComposition { sources: RecipientSource[]; excluded: string[] }
export const emptyComposition = (): RecipientComposition => ({ sources: [], excluded: [] });

// Trim, normalize IDN domains, and compare the entire address case-insensitively.
// Do not collapse dots or plus tags: those may identify different mailboxes.
export function recipientEmail(value: string): string | null {
  if (/[<>(),;:\\"\u0000-\u0020\u007f]/.test(value.trim())) return null;
  return normalizeDeliveryEmail(value);
}
// Match the existing account test-email policy: only the verified primary address.
export function verifiedSenderEmail(address: { emailAddress: string; verification?: { status?: string | null } | null } | null | undefined): string | null {
  return address?.verification?.status === "verified" && address.emailAddress.length <= 254 ? recipientEmail(address.emailAddress) : null;
}
export function resolveRecipients<T extends EmailRecipient>(to: readonly T[], cc: readonly T[] = [], excluded: readonly string[] = []) {
  const seen = new Set<string>(), removed = new Set(excluded);
  let duplicates = 0;
  const field = (entries: readonly T[]) => entries.flatMap(entry => {
    const email = recipientEmail(entry.email);
    if (!email) throw new Error(`Invalid email address: ${entry.email}`);
    if (removed.has(email)) return [];
    if (seen.has(email)) { duplicates++; return []; }
    seen.add(email);
    return [{ ...entry, email }];
  });
  const resolvedTo = field(to), resolvedCc = field(cc);
  return { to: resolvedTo, cc: resolvedCc, duplicates, count: seen.size };
}
export function resolveComposition(composition: RecipientComposition) {
  // A removed saved contribution stays excluded when the user explicitly
  // re-adds that address manually, including into the other header field.
  const entries = (field: "to" | "cc") => composition.sources.flatMap(source => source[field].filter(recipient =>
    source.key.startsWith("manual:") || !composition.excluded.includes(recipientEmail(recipient.email)!)));
  return resolveRecipients(entries("to"), entries("cc"));
}
export function toggleSource(composition: RecipientComposition, source: RecipientSource): RecipientComposition {
  return { ...composition, sources: composition.sources.some(item => item.key === source.key)
    ? composition.sources.filter(item => item.key !== source.key) : [...composition.sources, structuredClone(source)] };
}
export function addManual(composition: RecipientComposition, field: "to" | "cc", recipient: EmailRecipient): RecipientComposition {
  const email = recipientEmail(recipient.email);
  if (!email) throw new Error("Enter a valid email address.");
  return { sources: [...composition.sources, { key: `manual:${crypto.randomUUID()}`, to: field === "to" ? [{ ...recipient, email }] : [], cc: field === "cc" ? [{ ...recipient, email }] : [] }], excluded: composition.excluded };
}
export function excludeRecipient(composition: RecipientComposition, email: string): RecipientComposition {
  const normalized = recipientEmail(email)!;
  const sources = composition.sources.map(source => source.key.startsWith("manual:") ? {
    ...source, to: source.to.filter(item => recipientEmail(item.email) !== normalized), cc: source.cc.filter(item => recipientEmail(item.email) !== normalized),
  } : source).filter(source => !source.key.startsWith("manual:") || source.to.length || source.cc.length);
  return { sources, excluded: [...new Set([...composition.excluded, normalized])] };
}
export function parseRecipientText(text: string): EmailRecipient[] {
  return text.split(/[,;\n]/).map(value => value.trim()).filter(Boolean).map(value => {
    const match = value.match(/^(.+?)\s*<([^<>]+)>$/);
    const email = recipientEmail(match ? match[2]! : value);
    const name = match ? match[1]!.trim() : "";
    if (!email || name.length > 200 || /[\u0000-\u001f\u007f]/.test(name)) throw new Error("Use email addresses or Name <email>, separated by commas or new lines.");
    return { email, name };
  });
}
export const recipientText = (recipients: readonly EmailRecipient[]) => recipients.map(item => item.name ? `${item.name} <${item.email}>` : item.email).join("\n");
